import { Alert, Form, Input, Row, UploadFile } from "antd"
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { SystemDescriptions } from "src/common/descriptions";
import { ServiceContract } from "src/common/models/contract";
import { ServiceDebitContract } from "src/common/models/serviceContractDebit";
import { ServiceContractPayment } from "src/common/models/serviceContractDebitPayment";
import { TransactionContractPayment } from "src/common/models/transactionContractPayment";
import { amountValidator, formatToCurrency, getInputRequiredRule } from "src/common/util";
import { Col16, Col24, Col8 } from "src/components/Columns";
import { CurrencyInput } from "src/components/CurrencyInput";
import { FormActionButtons } from "src/components/FormActionButtons";
import { UploadComponent } from "src/components/UploadComponent";
import { FileType } from "src/components/UploadComponent/utils";
import { getCashboxStmtRecordsApi } from "src/features/CustomerPayment/state/action";
import { CREATE_CONTRACT_PAYMENT_API } from "src/features/CustomerPayment/state/actionTypes";
import { ServiceContractError } from "src/features/ServiceContract/state/types";
import { RootState } from "src/state/reducer";

interface Props {
    initialValues?: ServiceContractPayment
    onFinish: (customer: TransactionContractPayment) => void
    onCancel: () => void
    contractDebit?: ServiceDebitContract
    type: "ACCOUNT_PAYABLES" | "ACCOUNT_RECEIVABLES"
}

export interface ReduxProps {
    currentContract?: Partial<ServiceContract>
    error?: ServiceContractError
    isCreatingPayment: boolean
    cashboxBalance: number
}

export enum ImageActions {
    CREATE = "CREATE",
    DELETE = "DELETE",
    DO_NOTHING = "DO_NOTHING",
}

export interface UpdateImageData {
    uid: string
    action: ImageActions
}

export const PaymentForm = (props: Props) => {
    const dispatch = useDispatch()

    const [paymentInvalidMsg, setPaymentInvalidMsg] = useState<string>('')
    const [fileList, setFileList] = useState<UploadFile[]>([])
    const [updateImagesData, setUpdateImagesData] = useState<UpdateImageData[]>([])

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        currentContract: state.serviceContract.currentContract,
        error: state.serviceContract.error,
        isCreatingPayment: serviceLoading(state, [CREATE_CONTRACT_PAYMENT_API]),
        cashboxBalance: state.contractPayment.cashboxBalance || 0,
    }))

    const descriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const [form] = Form.useForm();

    useEffect(() => {
        form.setFieldsValue({
            previousBalance: props.contractDebit
                ? props.contractDebit.amount - props.contractDebit.amountPayed
                : reduxProps.currentContract?.total,
            newBalance: 0,
            cash: 0,
            transaction: 0,
            disscount: 0,
            total: 0,
        })

        if (props.type === "ACCOUNT_PAYABLES") {
            dispatch(getCashboxStmtRecordsApi({
                filter: "CASHBOX_BALANCE",
            }))
        }
    }, [])

    const onFinish = async (formValues) => {
        const imagesToCreate: UploadFile<any>[] = []

        updateImagesData.forEach(item => {
            if (item.action === ImageActions.CREATE) {
                const foundImage = fileList.find(image => image.uid === item.uid)
                if (foundImage) {
                    imagesToCreate.push(foundImage)
                }
            }
        })

        const total: number = Number(formValues.total)
        if (total === 0 || total > Number(formValues.previousBalance)) {
            return
        }

        const payment: TransactionContractPayment = {
            amount: formValues.total,
            cash: Number(formValues.cash),
            transaction: Number(formValues.transaction),
            disscount: Number(formValues.disscount),
            comment: formValues.comment,
            contractId: props.contractDebit?.id || 0,
            imagesToUpdate: updateImagesData.filter(item => item.action === ImageActions.DELETE),
        }

        if (formValues.origin) {
            payment.origin = formValues.origin
        }

        if (imagesToCreate.length > 0) {
            payment.imagesToCreate = await Promise.all(fileList.map(image => getImageBase64(image)))
        }

        props.onFinish(payment)
    }

    const getImageBase64 = async (image: any): Promise<string> => {
        return await getBase64(image.originFileObj as any)
    }

    const getBase64 = (file: FileType): Promise<string> => (
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file as any);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        })
    )

    const getButtonStatus = (): boolean => {
        const { total } = form.getFieldsValue()

        return props.initialValues
            ? false
            : !form.isFieldsTouched(["cash", "transaction", "disscount"]) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0 || paymentInvalidMsg.trim().length > 0 || total === 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
            actionLoading={reduxProps.isCreatingPayment}
        />
    )

    const handlePaymentChange = () => {
        const { cash, transaction, disscount, previousBalance } = form.getFieldsValue()
        const total: number = Number(cash) + Number(transaction) + Number(disscount)
        let newBalance: number = Number(previousBalance) - total

        if (newBalance < 0 || isNaN(total)) {
            newBalance = 0
            setPaymentInvalidMsg(descriptions.PAYMENT_GREATER_THAN_TOTAL)
        } else {
            setPaymentInvalidMsg('')
        }

        form.setFieldsValue({
            total,
            newBalance,
        })
    }

    const onChangeFilesList = (fileList: UploadFile[]) => {
        const newImagesData: UpdateImageData[] = []

        fileList.forEach(file => {
            if (!props.initialValues?.receiptData?.find(candidate => candidate.publicId === file.uid)) {
                newImagesData.push({
                    uid: file.uid,
                    action: ImageActions.CREATE,
                })
            } else {
                newImagesData.push({
                    uid: file.uid,
                    action: ImageActions.DO_NOTHING,
                })
            }
        })

        props.initialValues?.receiptData?.forEach(image => {
            if (!fileList.find(candidate => candidate.uid === image.publicId)) {
                newImagesData.push({
                    uid: image.publicId,
                    action: ImageActions.DELETE,
                })
            }
        })

        setUpdateImagesData(newImagesData)

        setFileList(fileList)
    }

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col8>
                    <Form.Item
                        label={
                            props.type === "ACCOUNT_PAYABLES"
                                ? `${descriptions.FORM.CASH.LABEL} ${descriptions.BALANCE} ${formatToCurrency(reduxProps.cashboxBalance.toString())}`
                                : `${descriptions.FORM.CASH.LABEL}`
                        }
                        name={"cash"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.CASH.LABEL),
                            {
                                validator: (_, value) => {
                                    return amountValidator(value, true)
                                }
                            },
                            {
                                validator: (_, value) => {
                                    if (props.type === "ACCOUNT_PAYABLES" && value > reduxProps.cashboxBalance) {
                                        return Promise.reject(descriptions.AMOUNT_MUST_BE_LET_BALANCE)
                                    }

                                    return Promise.resolve()
                                }
                            },
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.CASH.PLACEHOLDER}
                            onChange={handlePaymentChange}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.TRANSACTION.LABEL}
                        name={"transaction"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.TRANSACTION.LABEL),
                            {
                                validator: (_, value) => {
                                    return amountValidator(value, true)
                                }
                            },
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.TRANSACTION.PLACEHOLDER}
                            onChange={handlePaymentChange}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.DISSCOUNT.LABEL}
                        name={"disscount"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.DISSCOUNT.LABEL),
                            {
                                validator: (_, value) => {
                                    return amountValidator(value, true)
                                }
                            },
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.DISSCOUNT.PLACEHOLDER}
                            onChange={handlePaymentChange}
                        />
                    </Form.Item>
                </Col8>
                <Col16>
                    <Form.Item
                        label={descriptions.FORM.COMMENT.LABEL}
                        name={"comment"}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.COMMENT.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col16>
                {
                    props.type === "ACCOUNT_PAYABLES" &&
                    <Col8>
                        <Form.Item
                            label={"Image"}
                            name={"comment"}
                            validateTrigger="onBlur"
                        >
                            <UploadComponent
                                placeholder={"Upload"}
                                onChangeFilesList={onChangeFilesList}
                                maxFiles={1}
                            />
                        </Form.Item>
                    </Col8>
                }
                <Col24>
                    <hr style={{ borderColor: "#fefefe" }} />
                </Col24>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.PAYMENT_TOTAL.LABEL}
                        name={"total"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.PAYMENT_TOTAL.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.PAYMENT_TOTAL.PLACEHOLDER}
                            readOnly
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.PREVIOUS_BALANCE.LABEL}
                        name={"previousBalance"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.PREVIOUS_BALANCE.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.PREVIOUS_BALANCE.PLACEHOLDER}
                            readOnly
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.NEW_BALANCE.LABEL}
                        name={"newBalance"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.NEW_BALANCE.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <CurrencyInput
                            placeholder={descriptions.FORM.NEW_BALANCE.PLACEHOLDER}
                            readOnly
                        />
                    </Form.Item>
                </Col8>
            </Row>
            {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {
                paymentInvalidMsg.trim().length > 0 &&
                <Alert
                    showIcon
                    description={paymentInvalidMsg}
                    type="warning"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form >
    )

    return (
        renderForm()
    )
}
